import { Box, Button, IconButton, SvgIcon, Tab, Tabs, Tooltip, Typography, useTheme } from '@mui/material';
import { SyntheticEvent, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CustomTabPanel } from './components/CustomTabPanel';
import ReportDialog from './components/shared/ReportDialog';
import { ReportNavItem, ReportTypesData } from './components/shared/ReportTypesData';
import { ReportTabType } from '../../enums/ReportTabType';
import ReportHistoryTab, { ReportHistoryTabHandle } from './components/ReportHistoryTab';
import UserReportsTab from './components/UserReportsTab';
import { UserContext } from './../../components/shared/useUser';
import useNswagClient from './../../hooks/api/useNswagClient';
import { ReportType } from './../../enums/ReportType';
import { ConfigurationName } from './../../enums/ConfigurationName';
import PageTitle from '../../components/shared/PageTitle';
import PageArea from '../../components/shared/PageArea';
import { RefreshCw05 } from './../../assets';
import { useConfigurations } from './../../components/shared/useConfigurations';

const Reporting = () => {
  const { t } = useTranslation('common');
  const [tab, setTab] = useState(0);
  const { user, selectedStore, hasPermissionTo, isAdmin } = useContext(UserContext);
  const theme = useTheme();
  const { isConfigurationEnabled } = useConfigurations();
  const isCogsEnabled = isConfigurationEnabled(ConfigurationName.COG);
  const [open, setOpen] = useState(false);
  const [selectedReport, setSelectedReport] = useState<ReportNavItem>(ReportTypesData[0]);
  const [filteredReports, setFilteredReports] = useState<ReportNavItem[]>([...ReportTypesData]);
  const [isRefreshingReports, setIsRefreshingReports] = useState(false);
  const reportHistoryTabRef = useRef<ReportHistoryTabHandle>(null);
  const franchisesThatRequirePermission = ['NE Apple Green GB', 'Chick-fil-A Singapore', 'Chick-fil-A UK'];
  const canViewRecipeDetails = hasPermissionTo(['RecipeDetailsRead']) ||
    !franchisesThatRequirePermission.includes(selectedStore?.franchiseName || '');
  const showOnlyForCFA = franchisesThatRequirePermission.includes(selectedStore?.franchiseName || '');

  useEffect(() => {
    if (selectedStore) {
      getFilteredReports();
    }
  }, [user, selectedStore, isCogsEnabled]);

  const handleClose = () => {
    setOpen(false);
  };

  const handleTabChange = (event: SyntheticEvent, newValue: number) => {
    setTab(newValue);
  };

  const handleGenerateClick = (reportType: string) => {
    const item = ReportTypesData.find(x => x.title === reportType);

    if (!item) return;

    setSelectedReport(item);
    setOpen(true);
  };

  const getFilteredReports = () => {
    let reports = [...ReportTypesData];

    // Per-report permission filtering.
    reports = reports.filter(x => hasPermissionTo([x.permission]));

    if (isAdmin() || selectedStore?.franchiseName === 'EG ON THE MOVE LTD') {
      // keep all remaining reports
    } else {
      reports = reports.filter(x => x.title !== ReportType.StockCountSummary);
    }
    if (!hasPermissionTo(['MadeInStoreRead'])) {
      reports = reports.filter(x => x.title !== ReportType.MadeInStore);
    }

    if (!selectedStore?.franchiseName?.toLowerCase().includes('amrest')) {
      reports = reports.filter(x => x.title !== ReportType.ItemUsage);
    }
    if (!canViewRecipeDetails) {
      reports = reports.filter(x => x.title !== ReportType.Margin);
    }
    if (!showOnlyForCFA) {
      reports = reports.filter(x => x.title !== ReportType.inventoryExtension
        && x.title !== ReportType.giveawaysSamples);
    }

    if (!hasPermissionTo(['ReportUserRead'])) {
      reports = reports.filter(x => x.title !== ReportType.UserReport);
    }
    if (!isCogsEnabled) {
      reports = reports.filter(x => x.title !== ReportType.Cogs);
    }

    setFilteredReports(reports);
  };

  return (
    <PageArea>
      <Box
        sx={{
          textAlign: 'left',
          color: 'black',
          fontWeight: 'bold',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '8px',
          alignSelf: 'stretch',
        }}>
        <PageTitle>{' ' + t('reporting.title')}</PageTitle>
        {
          tab === ReportTabType.ReportHistory &&
          <Tooltip title={t('reporting.refresh')}>
            <span>
              <IconButton onClick={() => reportHistoryTabRef.current?.refresh()}
                disabled={isRefreshingReports}
                aria-label={t('reporting.refresh')}>
                <SvgIcon sx={isRefreshingReports ? { animation: 'spin 1s linear infinite', '@keyframes spin': { from: { transform: 'rotate(0deg)' }, to: { transform: 'rotate(360deg)' } } } : undefined}>
                  <RefreshCw05 />
                </SvgIcon>
              </IconButton>
            </span>
          </Tooltip>
        }
      </Box>
      <Box sx={{
        py: '12px',
        color: 'black',
        fontWeight: 'bold',
      }}>
        <Tabs aria-label="Report tabs"
          value={tab}
          onChange={handleTabChange}>
          <Tab label={t('reporting.generate-report')} />
          <Tab label={t('reporting.reports')} />
          {
            isAdmin() && <Tab label={t('reporting.user-reports')} />
          }
        </Tabs>
      </Box>
      <Box sx={{
        display: 'flex', flexDirection: 'column', width: '100%', height: '100%', p: '12px', border: `solid 1px ${theme.palette.common.white}`,
        borderRadius: '4px',
      }}>
        <CustomTabPanel value={tab}
          index={ReportTabType.GenerateReport}>
          {
            filteredReports.map((item) => {
              return (
                <Box key={item.title}
                  sx={{
                    display: 'flex',
                    width: '100%',
                    minHeight: '60px',
                    p: '12px',
                    alignItems: 'center',
                    justifyContent: 'left',
                    mb: '12px',
                    border: `solid 1px ${theme.palette.custom.gray[200]}`,
                    borderRadius: '4px',
                  }}>
                  <SvgIcon sx={{ width: '20px', mr: '24px' }}>{item.icon}</SvgIcon>
                  <Typography variant="textMD"
                    sx={{ display: 'flex', flex: 1, justifyContent: 'left' }}>{t(item.title)}</Typography>
                  <Button size='lg'
                    onClick={() => handleGenerateClick(item.title)}>{t('reporting.generate')}</Button>
                </Box>
              );
            })
          }
        </CustomTabPanel>
        <CustomTabPanel value={tab}
          index={ReportTabType.ReportHistory}>
          <ReportHistoryTab ref={reportHistoryTabRef}
            onLoadingChange={setIsRefreshingReports} />
        </CustomTabPanel>
        <CustomTabPanel value={tab}
          index={ReportTabType.UserReports}>
          <UserReportsTab />
        </CustomTabPanel>
      </Box>
      <ReportDialog
        open={open}
        onClose={handleClose}
        reportItem={selectedReport}
      />
    </PageArea>
  );
};

export default Reporting;